import React, { useState, useMemo } from 'react';
import { CASING_DB } from '../data/ingredients';
import styles from './Tools.module.css';

// ── Tool registry ─────────────────────────────────────────────
const TOOLS = [
  { id: 'spe',          icon: '🧪', title: 'SPE Calculator',        subtitle: 'Soy Protein Emulsion — fat replacement' },
  { id: 'fatreduction', icon: '📉', title: 'Fat Reduction Planner',  subtitle: 'Replace back fat with SPE to hit a target fat %' },
  { id: 'sodium',       icon: '🧂', title: 'Sodium → Salt Converter',subtitle: 'Check total salt from high-sodium ingredients' },
  { id: 'brine',        icon: '🫙', title: 'Brine Calculator',       subtitle: 'Wet brine % for injections and soaks' },
  { id: 'smoke',        icon: '💨', title: 'Smoke Time Estimator',   subtitle: 'Estimated cook time by casing diameter and method' },
];

// ── Main component ────────────────────────────────────────────
export default function Tools({ calc, onClose }) {
  const [activeTool, setActiveTool] = useState('spe');
  const tool = TOOLS.find(t => t.id === activeTool) || TOOLS[0];

  return (
    <div className={styles.overlay}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon}>🔧</span>
          <div>
            <div className={styles.headerTitle}>Tools & Calculators</div>
            <div className={styles.headerSub}>Standalone calculators that feed results into your recipe</div>
          </div>
        </div>
        <button className={styles.closeBtn} onClick={onClose}>✕ Close</button>
      </div>

      <div className={styles.body}>
        <nav className={styles.nav}>
          {TOOLS.map(t => (
            <button
              key={t.id}
              className={`${styles.navItem} ${activeTool === t.id ? styles.navActive : ''}`}
              onClick={() => setActiveTool(t.id)}
            >
              <span className={styles.navIcon}>{t.icon}</span>
              <div className={styles.navText}>
                <span className={styles.navLabel}>{t.title}</span>
                <span className={styles.navSub}>{t.subtitle}</span>
              </div>
            </button>
          ))}
        </nav>

        <div className={styles.content}>
          <div className={styles.toolHeader}>
            <span className={styles.toolIcon}>{tool.icon}</span>
            <div>
              <h2 className={styles.toolTitle}>{tool.title}</h2>
              <p  className={styles.toolSubtitle}>{tool.subtitle}</p>
            </div>
          </div>

          {activeTool === 'spe'          && <SPECalculator       calc={calc} />}
          {activeTool === 'fatreduction' && <FatReductionPlanner calc={calc} />}
          {activeTool === 'sodium'       && <SodiumSaltConverter calc={calc} />}
          {activeTool === 'brine'        && <BrineCalculator     calc={calc} />}
          {activeTool === 'smoke'        && <SmokeTimeEstimator  calc={calc} />}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TOOL 1 — SPE Calculator
// ═══════════════════════════════════════════════════════════════
function SPECalculator({ calc }) {
  const { meatWeightG, rows, updateRow, addRow } = calc;
  const [targetMode,  setTargetMode]  = useState('pct');
  const [targetPct,   setTargetPct]   = useState('10');
  const [targetGrams, setTargetGrams] = useState('');
  const [ratio,       setRatio]       = useState({ spi: 1, oil: 4, water: 5 });
  const [added,       setAdded]       = useState(false);

  const speGrams = useMemo(() => {
    if (targetMode === 'grams') return parseFloat(targetGrams) || 0;
    return meatWeightG > 0 ? Math.round((parseFloat(targetPct)||0)/100 * meatWeightG * 10)/10 : 0;
  }, [targetMode, targetPct, targetGrams, meatWeightG]);

  const total  = (ratio.spi + ratio.oil + ratio.water) || 10;
  const spiG   = Math.round(speGrams * ratio.spi   / total * 10) / 10;
  const oilG   = Math.round(speGrams * ratio.oil   / total * 10) / 10;
  const watG   = Math.round(speGrams * ratio.water / total * 10) / 10;
  const spePct = meatWeightG > 0 ? Math.round(speGrams / meatWeightG * 100 * 100)/100 : 0;

  const handleAddToRecipe = () => {
    if (!meatWeightG || speGrams <= 0) return;
    const fatRows = (rows || []).filter(r => r.sectionKey === 'fat');
    const speRow  = fatRows.find(r => r.name === 'Soy Protein Emulsion');
    const empty   = fatRows.find(r => !r.name);
    const target  = speRow || empty;
    if (target) {
      updateRow(target.id, 'name', 'Soy Protein Emulsion');
      updateRow(target.id, 'pct',  spePct.toString());
      updateRow(target.id, 'note', `${spiG}g SPI + ${oilG}g oil + ${watG}g water`);
    } else {
      addRow('fat');
      setTimeout(() => {
        const updated = (calc.rows||[]).filter(r => r.sectionKey === 'fat');
        const newest  = updated[updated.length - 1];
        if (newest) {
          updateRow(newest.id, 'name', 'Soy Protein Emulsion');
          updateRow(newest.id, 'pct',  spePct.toString());
          updateRow(newest.id, 'note', `${spiG}g SPI + ${oilG}g oil + ${watG}g water`);
        }
      }, 60);
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  return (
    <div className={styles.toolBody}>
      <Callout type="info">
        SPE is a 1:4:5 mix of soy protein isolate, vegetable oil, and water — a white
        cream-cheese-like gel that replaces animal fat gram-for-gram.
        Always use <strong>SPI (≥90% protein)</strong>, not SPC — SPC won't form a proper gel at this ratio.
      </Callout>

      <Section title="Target Amount">
        <div className={styles.modeToggle}>
          {[['pct','% of meat weight'],['grams','Fixed grams']].map(([id,lbl]) => (
            <button key={id}
              className={`${styles.modeBtn} ${targetMode===id?styles.modeBtnActive:''}`}
              onClick={() => setTargetMode(id)}>{lbl}</button>
          ))}
        </div>
        {targetMode === 'pct'
          ? <InputRow label="SPE % of meat weight" unit="%" hint="Recommended: 5–20%">
              <input className={styles.numInput} type="text" inputMode="decimal"
                value={targetPct} onChange={e => setTargetPct(e.target.value)} />
            </InputRow>
          : <InputRow label="Total SPE grams" unit="g">
              <input className={styles.numInput} type="text" inputMode="decimal"
                placeholder="e.g. 200" value={targetGrams}
                onChange={e => setTargetGrams(e.target.value)} />
            </InputRow>}
      </Section>

      <Section title="SPI : Oil : Water Ratio">
        <p className={styles.sectionNote}>Standard is 1:4:5. Adjust only if your SPI product specifies otherwise.</p>
        <div className={styles.ratioRow}>
          {[['spi','SPI'],['oil','Oil'],['water','Water']].map(([k,lbl]) => (
            <div key={k} className={styles.ratioField}>
              <label className={styles.ratioLabel}>{lbl}</label>
              <input className={styles.ratioInput} type="text" inputMode="decimal"
                value={ratio[k]}
                onChange={e => setRatio(p => ({...p, [k]: parseFloat(e.target.value)||0}))} />
            </div>
          ))}
        </div>
      </Section>

      {speGrams > 0 && (
        <Section title="Batch Breakdown">
          <div className={styles.results}>
            <ResultRow label="Total SPE" value={`${speGrams}g`} highlight />
            <ResultRow label={`Soy Protein Isolate (1 part)`}  value={`${spiG}g`} />
            <ResultRow label={`Vegetable Oil (${ratio.oil} parts)`} value={`${oilG}g`} />
            <ResultRow label={`Water (${ratio.water} parts)`}  value={`${watG}g`} />
            {meatWeightG > 0 && <ResultRow label="As % of meat+fat basis" value={`${spePct}%`} highlight />}
          </div>
          <Callout type="method">
            <strong>How to make it:</strong>
            <ol style={{margin:'var(--space-2) 0 0 var(--space-4)',padding:0,display:'flex',flexDirection:'column',gap:'var(--space-2)'}}>
              <li>Using a food processor, blender, or manual whisk, combine <strong>{watG}g cold water</strong> with <strong>{spiG}g soy protein isolate</strong> and mix until a shiny paste is formed.</li>
              <li>Add <strong>{oilG}g chilled vegetable oil</strong> and cut at high speed until a stable, smooth emulsion is obtained. The result should look and feel like soft cream cheese.</li>
              <li>Store refrigerated for up to 5 days, or freeze for longer storage. Use cold when adding to your sausage mix.</li>
            </ol>
          </Callout>
          <button className={`${styles.addBtn} ${added?styles.addBtnDone:''}`}
            onClick={handleAddToRecipe} disabled={!meatWeightG || speGrams <= 0}>
            {added ? '✓ Added to Recipe' : '→ Add to Recipe (Fat section)'}
          </button>
          {!meatWeightG && <p className={styles.addHint}>Set meat weight in the calculator first.</p>}
        </Section>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TOOL 2 — Fat Reduction Planner
// ═══════════════════════════════════════════════════════════════
function FatReductionPlanner({ calc }) {
  const { meatWeightG, filledIngredients, totals, rows, updateRow, addRow, nutrition } = calc;
  const [targetFatPct, setTargetFatPct] = useState('10');
  const [applied,      setApplied]      = useState(false);

  const currentFatRatio = nutrition?.fatRatioTotal  || 0;
  const currentMFRatio  = nutrition?.fatRatioMFOnly || 0;
  const totalFormulaG   = totals?.totalG || 0;
  const currentFatG     = Math.round(currentFatRatio / 100 * totalFormulaG * 10) / 10;
  const target          = parseFloat(targetFatPct) || 0;
  const targetFatG      = Math.round(target / 100 * totalFormulaG * 10) / 10;
  const fatToRemoveG    = Math.max(0, Math.round((currentFatG - targetFatG) * 10) / 10);

  // Back fat is ~88.7% fat by weight
  const backFatToRemoveG   = Math.round(fatToRemoveG / 0.887 * 10) / 10;
  const backFatToRemovePct = meatWeightG > 0
    ? Math.round(backFatToRemoveG / meatWeightG * 100 * 1000) / 1000 : 0;

  const speNeededG  = backFatToRemoveG;
  const spePct      = meatWeightG > 0
    ? Math.round(speNeededG / meatWeightG * 100 * 1000) / 1000 : 0;

  const backFatRow       = (filledIngredients||[]).find(r =>
    r.sectionKey === 'fat' &&
    (r.name.toLowerCase().includes('back fat') || r.name.toLowerCase().includes('back  fat') ||
     r.name.toLowerCase().includes('lard')     || r.name.toLowerCase().includes('fat'))
  );
  const currentBackFatPct = parseFloat(backFatRow?.pct) || 0;
  const newBackFatPct     = Math.max(0, Math.round((currentBackFatPct - backFatToRemovePct)*1000)/1000);
  const canReduce         = currentFatRatio > 0 && target < currentFatRatio && totalFormulaG > 0;

  const handleApply = () => {
    if (!canReduce || !backFatRow) return;
    updateRow(backFatRow.id, 'pct', newBackFatPct.toString());
    const fatRows  = (rows||[]).filter(r => r.sectionKey === 'fat');
    const speRow   = fatRows.find(r => r.name === 'Soy Protein Emulsion');
    const empty    = fatRows.find(r => !r.name && r.id !== backFatRow?.id);
    const tgt      = speRow || empty;
    if (tgt) {
      updateRow(tgt.id, 'name', 'Soy Protein Emulsion');
      updateRow(tgt.id, 'pct',  spePct.toString());
    } else {
      addRow('fat');
      setTimeout(() => {
        const updated = (calc.rows||[]).filter(r => r.sectionKey === 'fat');
        const newest  = updated[updated.length - 1];
        if (newest) {
          updateRow(newest.id, 'name', 'Soy Protein Emulsion');
          updateRow(newest.id, 'pct',  spePct.toString());
        }
      }, 60);
    }
    setApplied(true);
    setTimeout(() => setApplied(false), 2500);
  };

  return (
    <div className={styles.toolBody}>
      <Callout type="info">
        Reads your current recipe and calculates how much back fat to remove and how much
        SPE to add to hit a target fat %. SPE replaces fat gram-for-gram and keeps
        total formula weight the same.
      </Callout>

      {totalFormulaG === 0 ? (
        <Callout type="warn">Build a recipe in the calculator first — this tool reads your current ingredients.</Callout>
      ) : (
        <>
          <Section title="Current Recipe">
            <div className={styles.results}>
              <ResultRow label="Total formula weight"       value={`${totalFormulaG.toFixed(0)}g`} />
              <ResultRow label="Fat — total sausage"        value={`${currentFatRatio.toFixed(1)}%`} highlight={currentFatRatio > 15} />
              <ResultRow label="Fat — meat+fat block only"  value={`${currentMFRatio.toFixed(1)}%`} />
              <ResultRow label="Total fat grams"            value={`${currentFatG}g`} />
            </div>
          </Section>

          <Section title="Target">
            <InputRow label="Target total fat %" unit="%" hint="Typical low-fat: 5–12%">
              <input className={styles.numInput} type="text" inputMode="decimal"
                value={targetFatPct}
                onChange={e => { setTargetFatPct(e.target.value); setApplied(false); }} />
            </InputRow>
          </Section>

          {canReduce && fatToRemoveG > 0 ? (
            <Section title="Reduction Plan">
              <div className={styles.results}>
                <ResultRow label="Fat to remove"              value={`${fatToRemoveG}g`} />
                <ResultRow label="Back fat to remove"         value={`${backFatToRemoveG}g  (−${backFatToRemovePct}%)`} />
                {backFatRow && (
                  <ResultRow label={`${backFatRow.name} — new %`} value={`${newBackFatPct}%`} />
                )}
                <ResultRow label="SPE to add" value={`${speNeededG}g  (${spePct}%)`} highlight />
              </div>
              {!backFatRow && (
                <div className={styles.warnBox}>
                  No fat row detected. Add a fat ingredient (back fat, lard) to the Fat section first.
                </div>
              )}
              <Callout type="method">
                Remove {backFatToRemoveG}g of back fat → replace with {speNeededG}g SPE (1:4:5).
                Fat drops from {currentFatRatio.toFixed(1)}% to ~{target}%.
                Total formula weight stays the same.
              </Callout>
              <button className={`${styles.addBtn} ${applied?styles.addBtnDone:''}`}
                onClick={handleApply} disabled={!backFatRow}>
                {applied ? '✓ Applied to Recipe' : '→ Apply to Recipe'}
              </button>
              {!backFatRow && <p className={styles.addHint}>Add a fat row to the recipe first.</p>}
            </Section>
          ) : canReduce && fatToRemoveG === 0 ? (
            <Callout type="info">Already at or below {target}% fat.</Callout>
          ) : currentFatRatio > 0 ? (
            <Callout type="info">Target ({target}%) must be lower than current ({currentFatRatio.toFixed(1)}%).</Callout>
          ) : (
            <Callout type="warn">Add ingredients with fat macros to the recipe to enable fat reduction planning.</Callout>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TOOL 3 — Sodium to Salt Converter
// ═══════════════════════════════════════════════════════════════
const SALT_FACTOR = 2.5421;

// Preset serving sizes with USDA gram weights per common volumetric serving
// gramsPerServing is the actual measured gram weight — NOT assumed to be 15g for all tbsp
const SODIUM_PRESETS = [
  { name: 'Soy Sauce (Kikkoman)',   sodiumPer100g: 5765, servingLabel: '1 tbsp', gramsPerServing: 15.8 },
  { name: 'Low-Sodium Soy Sauce',   sodiumPer100g: 3200, servingLabel: '1 tbsp', gramsPerServing: 15.7 },
  { name: 'Worcestershire Sauce',   sodiumPer100g:  980, servingLabel: '1 tbsp', gramsPerServing: 16.6 },
  { name: 'Fish Sauce',             sodiumPer100g: 5170, servingLabel: '1 tbsp', gramsPerServing: 16.1 },
  { name: 'Miso Paste (white)',     sodiumPer100g: 3520, servingLabel: '1 tbsp', gramsPerServing: 17.7 },
  { name: 'Oyster Sauce',           sodiumPer100g: 2733, servingLabel: '1 tbsp', gramsPerServing: 17.6 },
  { name: 'Liquid Smoke',           sodiumPer100g:  170, servingLabel: '1 tsp',  gramsPerServing:  4.8 },
  { name: 'Dijon Mustard',          sodiumPer100g: 1240, servingLabel: '1 tsp',  gramsPerServing:  5.3 },
  { name: 'Custom ingredient',      sodiumPer100g:    0, servingLabel: 'custom', gramsPerServing:    0 },
];

function SodiumSaltConverter({ calc }) {
  const { meatWeightG, filledIngredients } = calc;
  const [labelMode,     setLabelMode]     = useState('perServing');
  const [preset,        setPreset]        = useState(SODIUM_PRESETS[0]);
  const [sodiumMgLabel, setSodiumMgLabel] = useState('911');
  const [servingGrams,  setServingGrams]  = useState('15.8');
  const [sodiumPer100g, setSodiumPer100g] = useState('5765');
  const [ingPct,        setIngPct]        = useState('2');

  const isCustom = preset.name === 'Custom ingredient';

  const sodiumVal = labelMode === 'per100g'
    ? (parseFloat(sodiumPer100g) || 0)
    : (() => {
        const mg = parseFloat(sodiumMgLabel) || 0;
        const g  = parseFloat(servingGrams)  || 0;
        return g > 0 ? Math.round(mg / g * 100 * 10) / 10 : 0;
      })();

  const ingPctVal       = parseFloat(ingPct) || 0;
  const ingGrams        = meatWeightG > 0 ? Math.round(ingPctVal/100*meatWeightG*10)/10 : 0;
  const sodiumFromIngMg = Math.round(ingGrams * sodiumVal / 100 * 10) / 10;
  const saltFromIngG    = Math.round(sodiumFromIngMg * SALT_FACTOR / 1000 * 1000) / 1000;
  const saltFromIngPct  = meatWeightG > 0
    ? Math.round(saltFromIngG / meatWeightG * 100 * 1000) / 1000 : 0;
  const saltFromSaltPct = (filledIngredients||[])
    .filter(r => r.sectionKey === 'salt')
    .reduce((sum,r) => sum + (parseFloat(r.pct)||0), 0);
  const totalSaltPct = Math.round((saltFromSaltPct + saltFromIngPct)*1000)/1000;
  const headroom     = Math.round((2.0 - totalSaltPct)*1000)/1000;
  const status       = totalSaltPct > 2.0 ? 'over' : totalSaltPct > 1.8 ? 'warn' : 'safe';

  const handlePreset = e => {
    const p = SODIUM_PRESETS.find(ps => ps.name === e.target.value);
    if (!p) return;
    setPreset(p);
    if (p.sodiumPer100g > 0) {
      setSodiumPer100g(p.sodiumPer100g.toString());
      const mg = Math.round(p.gramsPerServing * p.sodiumPer100g / 100);
      setSodiumMgLabel(mg.toString());
      setServingGrams(p.gramsPerServing.toString());
    }
  };

  return (
    <div className={styles.toolBody}>
      <Callout type="info">
        Salt (NaCl) is 39.34% sodium by weight. Formula: <strong>Na (mg) × 2.54 = Salt (mg)</strong>.
        Enter values directly from your nutrition label — serving size is converted automatically.
      </Callout>

      <Section title="Ingredient">
        <InputRow label="Ingredient">
          <select className={styles.select} value={preset.name} onChange={handlePreset}>
            {SODIUM_PRESETS.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
          </select>
        </InputRow>
      </Section>

      <Section title="How to read the sodium value">
        <div className={styles.modeToggle}>
          {[
            ['perServing','From label (mg per serving)'],
            ['per100g',   'Per 100g (metric labels)'],
          ].map(([id,lbl]) => (
            <button key={id}
              className={`${styles.modeBtn} ${labelMode===id?styles.modeBtnActive:''}`}
              onClick={() => setLabelMode(id)}>{lbl}</button>
          ))}
        </div>

        {labelMode === 'perServing' && (
          <>
            <Callout type="info">
              Enter sodium and serving size exactly as shown on the label.
              The gram weight accounts for density — a tablespoon of soy sauce (~15.8g) is denser than water.
              USDA reference gram weights are pre-filled for the presets above.
            </Callout>
            <InputRow label="Sodium per serving (label)" unit="mg">
              <input className={styles.numInput} type="text" inputMode="decimal"
                value={sodiumMgLabel} onChange={e => setSodiumMgLabel(e.target.value)} />
            </InputRow>
            <InputRow label="Serving size" unit="g"
              hint={!isCustom && preset.gramsPerServing > 0
                ? `${preset.servingLabel} of ${preset.name} ≈ ${preset.gramsPerServing}g (USDA)`
                : 'Weigh one serving, or use label gram weight'}>
              <input className={styles.numInput} type="text" inputMode="decimal"
                value={servingGrams} onChange={e => setServingGrams(e.target.value)} />
            </InputRow>
            {sodiumVal > 0 && (
              <div className={styles.derivedNote}>
                → Derived: <strong>{sodiumVal.toFixed(0)}mg sodium per 100g</strong>
                {!isCustom && preset.sodiumPer100g > 0 && (
                  <span className={styles.derivedCheck}>
                    {Math.abs(sodiumVal - preset.sodiumPer100g) < 300
                      ? ` ✓ close to USDA reference (${preset.sodiumPer100g}mg/100g)`
                      : ` ⚠ differs from USDA (${preset.sodiumPer100g}mg/100g) — verify serving weight`}
                  </span>
                )}
              </div>
            )}
          </>
        )}
        {labelMode === 'per100g' && (
          <InputRow label="Sodium per 100g" unit="mg" hint="From metric label or USDA FoodData Central">
            <input className={styles.numInput} type="text" inputMode="decimal"
              value={sodiumPer100g} onChange={e => setSodiumPer100g(e.target.value)} />
          </InputRow>
        )}
      </Section>

      <Section title="Recipe Usage">
        <InputRow label="Ingredient % of meat weight" unit="%"
          hint={meatWeightG > 0 ? `= ${ingGrams}g in this batch` : ''}>
          <input className={styles.numInput} type="text" inputMode="decimal"
            value={ingPct} onChange={e => setIngPct(e.target.value)} />
        </InputRow>
      </Section>

      {sodiumVal > 0 && ingPctVal > 0 && (
        <Section title="Salt Contribution Analysis">
          <div className={styles.results}>
            <ResultRow label="Sodium from this ingredient"          value={`${sodiumFromIngMg.toFixed(1)} mg`} />
            <ResultRow label="Salt equivalent  (Na × 2.54 ÷ 1000)" value={`${saltFromIngG} g`} />
            <ResultRow label="As % of meat weight"                  value={`${saltFromIngPct}%`} />
          </div>
          <div className={styles.saltTotalCard}>
            <div className={styles.saltTotalRow}>
              <span>Salt — Salt section</span>
              <span className={styles.saltVal}>{saltFromSaltPct.toFixed(3)}%</span>
            </div>
            <div className={styles.saltTotalRow}>
              <span>Salt — {isCustom ? 'this ingredient' : preset.name}</span>
              <span className={styles.saltVal}>{saltFromIngPct}%</span>
            </div>
            <div className={`${styles.saltTotalRow} ${styles.saltTotalFinal} ${styles[status]}`}>
              <span>Total salt</span>
              <span className={styles.saltTotalVal}>{totalSaltPct}%</span>
            </div>
            <div className={`${styles.saltStatus} ${styles[status]}`}>
              {status === 'over' && `❌ Over limit by ${Math.abs(headroom).toFixed(3)}%`}
              {status === 'warn' && `⚠️ Approaching limit — ${headroom.toFixed(3)}% headroom`}
              {status === 'safe' && `✅ Within range — ${headroom.toFixed(3)}% headroom`}
            </div>
          </div>
          {saltFromSaltPct === 0 && (
            <Callout type="warn">No salt in the Salt section yet. Add table/kosher salt first.</Callout>
          )}
        </Section>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TOOL 4 — Brine Calculator
// ═══════════════════════════════════════════════════════════════
function BrineCalculator({ calc }) {
  const { meatWeightG } = calc;
  const [brineMode,    setBrineMode]    = useState('wet');    // 'wet' | 'pump'
  const [waterG,       setWaterG]       = useState('1000');
  const [targetBrinePct, setTargetBrinePct] = useState('10');
  const [targetSaltInMeat, setTargetSaltInMeat] = useState('1.75');
  const [pumpPct,      setPumpPct]      = useState('10');

  const brinePctVal        = parseFloat(targetBrinePct)   || 0;
  const waterGVal          = parseFloat(waterG)           || 0;
  const targetSaltInMeatV  = parseFloat(targetSaltInMeat) || 0;
  const pumpPctVal         = parseFloat(pumpPct)          || 0;

  // Wet brine: salt_g = brine_pct / (100 - brine_pct) * water_g
  const saltForBrineG      = Math.round(brinePctVal / (100 - brinePctVal) * waterGVal * 10) / 10;
  const totalBrineG        = Math.round((waterGVal + saltForBrineG) * 10) / 10;
  const saltInWaterPct     = Math.round(saltForBrineG / totalBrineG * 100 * 10) / 10;

  // Equilibrium: after long soak (24–48h), meat salt ≈ brine_pct × 0.4–0.55 (rough equilibrium factor)
  // For practical use: target brine pct for desired meat salt pct
  const brineForTargetSalt = Math.round(targetSaltInMeatV / 0.47 * 100) / 100;  // 0.47 = midpoint equilibrium factor

  // Pump / injection method
  // Salt in meat (%) = brine_pct × pump_pct / 100
  const saltInMeatFromPump = meatWeightG > 0
    ? Math.round(brinePctVal * pumpPctVal / 100 * 100) / 100 : 0;
  const brineNeededG       = meatWeightG > 0 ? Math.round(meatWeightG * pumpPctVal / 100 * 10)/10 : 0;

  // Salt needed for pump brine
  const saltForPumpBrineG  = Math.round(brinePctVal / (100 - brinePctVal) * brineNeededG * 10) / 10;

  return (
    <div className={styles.toolBody}>
      <Callout type="info">
        Wet brining uses osmosis to season whole muscles (hams, poultry, roasts).
        Use a higher brine % than your target meat salt — the meat will equilibrate
        to roughly 40–55% of the brine concentration over 24–48 hours.
      </Callout>

      <Section title="Method">
        <div className={styles.modeToggle}>
          {[['wet','Wet soak brine'],['pump','Injection / pump']].map(([id,lbl]) => (
            <button key={id}
              className={`${styles.modeBtn} ${brineMode===id?styles.modeBtnActive:''}`}
              onClick={() => setBrineMode(id)}>{lbl}</button>
          ))}
        </div>
      </Section>

      {brineMode === 'wet' && (
        <>
          <Section title="Brine Composition">
            <InputRow label="Water" unit="g" hint="Use cold water or weigh ice">
              <input className={styles.numInput} type="text" inputMode="decimal"
                value={waterG} onChange={e => setWaterG(e.target.value)} />
            </InputRow>
            <InputRow label="Target brine %" unit="%" hint="10% is standard for sausage/pork; 5–6% for poultry">
              <input className={styles.numInput} type="text" inputMode="decimal"
                value={targetBrinePct} onChange={e => setTargetBrinePct(e.target.value)} />
            </InputRow>
          </Section>

          {brinePctVal > 0 && waterGVal > 0 && (
            <Section title="Results">
              <div className={styles.results}>
                <ResultRow label="Salt to add"     value={`${saltForBrineG}g`} highlight />
                <ResultRow label="Total brine"     value={`${totalBrineG}g`} />
                <ResultRow label="Actual brine %"  value={`${saltInWaterPct}%`} />
              </div>
              <Callout type="method">
                Dissolve {saltForBrineG}g of non-iodized salt in {waterGVal}g cold water.
                Submerge meat fully. Refrigerate 24–48 hours, turning once.
                Pat dry before smoking. Target meat salt after equilibrium: ~{(brinePctVal * 0.47).toFixed(2)}%.
              </Callout>
            </Section>
          )}

          <Section title="Reverse: Target Salt in Meat">
            <InputRow label="Desired salt in finished meat" unit="%" hint="Typical: 1.5–2.0%">
              <input className={styles.numInput} type="text" inputMode="decimal"
                value={targetSaltInMeat} onChange={e => setTargetSaltInMeat(e.target.value)} />
            </InputRow>
            {targetSaltInMeatV > 0 && (
              <div className={styles.results} style={{marginTop:'var(--space-2)'}}>
                <ResultRow label="Recommended brine %" value={`${brineForTargetSalt}%`} highlight />
                <div className={styles.resultNote}>
                  Based on ~47% equilibrium factor. Verify with your specific cut and timing.
                </div>
              </div>
            )}
          </Section>
        </>
      )}

      {brineMode === 'pump' && (
        <>
          <Section title="Injection Parameters">
            <InputRow label="Brine %" unit="%" hint="10–15% typical injection brine">
              <input className={styles.numInput} type="text" inputMode="decimal"
                value={targetBrinePct} onChange={e => setTargetBrinePct(e.target.value)} />
            </InputRow>
            <InputRow label="Pump %" unit="%" hint="Brine weight as % of meat weight. 10% = +100g per kg">
              <input className={styles.numInput} type="text" inputMode="decimal"
                value={pumpPct} onChange={e => setPumpPct(e.target.value)} />
            </InputRow>
          </Section>

          {brinePctVal > 0 && pumpPctVal > 0 && (
            <Section title="Results">
              <div className={styles.results}>
                <ResultRow label="Salt in finished meat"  value={`${saltInMeatFromPump}%`} highlight />
                {meatWeightG > 0 && <>
                  <ResultRow label="Brine to inject"      value={`${brineNeededG}g`} />
                  <ResultRow label="Salt for that brine"  value={`${saltForPumpBrineG}g`} />
                  <ResultRow label="Water for that brine" value={`${Math.round((brineNeededG - saltForPumpBrineG)*10)/10}g`} />
                </>}
              </div>
              {!meatWeightG && (
                <p className={styles.addHint}>Set meat weight in the calculator for gram amounts.</p>
              )}
            </Section>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TOOL 5 — Smoke Time Estimator
// ═══════════════════════════════════════════════════════════════
function SmokeTimeEstimator({ calc }) {
  const { selectedCasing } = calc;
  const [diamMode,    setDiamMode]    = useState('casing');   // 'casing' | 'manual'
  const [manualDiam,  setManualDiam]  = useState('35');       // mm
  const [smokerTemp,  setSmokerTemp]  = useState('175');      // °F
  const [targetTemp,  setTargetTemp]  = useState('145');      // °F internal
  const [method,      setMethod]      = useState('hot');      // 'hot' | 'cold'

  // Get diameter from selected casing or manual entry
  const casingEntry = selectedCasing !== null ? CASING_DB[selectedCasing] : null;
  const diamMM      = diamMode === 'casing' && casingEntry
    ? casingEntry[1]
    : parseFloat(manualDiam) || 35;
  const diamIn      = diamMM / 25.4;

  const smokerTempV = parseFloat(smokerTemp) || 175;
  const targetTempV = parseFloat(targetTemp) || 145;

  // Time estimate — based on minutes per inch of diameter at given smoker temp
  // At 175°F smoker: ~35–50 min/inch for target 145°F internal
  // At 200°F smoker: ~25–38 min/inch
  // At 225°F smoker: ~20–30 min/inch
  // Scaling factor based on temperature
  const tempFactor   = Math.max(0.5, (225 - smokerTempV) / 50 + 0.8);
  const baseMinPerIn = { low: 35, high: 50 };
  const minLow  = Math.round(diamIn * baseMinPerIn.low  * tempFactor);
  const minHigh = Math.round(diamIn * baseMinPerIn.high * tempFactor);

  // Cold smoke schedule (incremental)
  const coldSchedule = [
    { temp: '100–110°F', duration: '60 min', note: 'Bloom / dry exterior — door slightly open' },
    { temp: '125°F',     duration: '60 min', note: 'Begin smoke introduction' },
    { temp: '150°F',     duration: '90 min', note: 'Continue smoke' },
    { temp: '170–185°F', duration: 'Until 145°F internal', note: 'Final cook' },
  ];

  // Internal temp safety thresholds
  const safeTemps = [
    { temp: 145, hold: '4 min',  note: 'USDA minimum for whole muscle and sausage' },
    { temp: 150, hold: '1 min',  note: 'Common target for smoked sausage' },
    { temp: 160, hold: 'instant',note: 'Fully cooked — no hold time required' },
  ];

  return (
    <div className={styles.toolBody}>
      <Callout type="info">
        Smoke times are estimates. Always verify doneness with a calibrated instant-read
        thermometer. The only safe measure is internal temperature — not time alone.
      </Callout>

      <Section title="Casing Diameter">
        <div className={styles.modeToggle}>
          {[['casing','From recipe casing'],['manual','Enter manually']].map(([id,lbl]) => (
            <button key={id}
              className={`${styles.modeBtn} ${diamMode===id?styles.modeBtnActive:''}`}
              onClick={() => setDiamMode(id)}>{lbl}</button>
          ))}
        </div>
        {diamMode === 'casing' && (
          casingEntry
            ? <div className={styles.casingDisplay}>
                <span className={styles.casingName}>{casingEntry[0]}</span>
                <span className={styles.casingDiam}>{diamMM}mm ({diamIn.toFixed(1)}")</span>
              </div>
            : <div className={styles.warnBox}>No casing selected in the recipe. Select one in the Casing Calculator or switch to manual entry.</div>
        )}
        {diamMode === 'manual' && (
          <InputRow label="Casing diameter" unit="mm">
            <input className={styles.numInput} type="text" inputMode="decimal"
              value={manualDiam} onChange={e => setManualDiam(e.target.value)} />
          </InputRow>
        )}
      </Section>

      <Section title="Cooking Method">
        <div className={styles.modeToggle}>
          {[['hot','Hot smoked'],['cold','Cold smoke (incremental)']].map(([id,lbl]) => (
            <button key={id}
              className={`${styles.modeBtn} ${method===id?styles.modeBtnActive:''}`}
              onClick={() => setMethod(id)}>{lbl}</button>
          ))}
        </div>
      </Section>

      {method === 'hot' && (
        <>
          <Section title="Smoker Settings">
            <InputRow label="Smoker/pit temperature" unit="°F" hint="175–185°F is standard for sausage">
              <input className={styles.numInput} type="text" inputMode="decimal"
                value={smokerTemp} onChange={e => setSmokerTemp(e.target.value)} />
            </InputRow>
            <InputRow label="Target internal temperature" unit="°F" hint="145°F minimum (USDA)">
              <input className={styles.numInput} type="text" inputMode="decimal"
                value={targetTemp} onChange={e => setTargetTemp(e.target.value)} />
            </InputRow>
          </Section>

          <Section title="Estimated Time">
            <div className={styles.results}>
              <ResultRow label="Diameter"            value={`${diamMM}mm  (${diamIn.toFixed(1)}")`} />
              <ResultRow label="Smoker temp"         value={`${smokerTempV}°F`} />
              <ResultRow label="Estimated cook time" value={`${minLow}–${minHigh} min`} highlight />
              <ResultRow label="Approx. hours"       value={`${(minLow/60).toFixed(1)}–${(minHigh/60).toFixed(1)} hrs`} />
            </div>
            <Callout type="method">
              Start checking internal temp at {minLow} minutes. Pull when your thermometer reads {targetTempV}°F.
              Immediately transfer to a cold-water bath until internal drops to 90–100°F, then bloom at room temp 3–4 hours.
            </Callout>
          </Section>
        </>
      )}

      {method === 'cold' && (
        <Section title="Incremental Temperature Schedule">
          <Callout type="warn">
            Cold smoking requires Cure #1 (or Cultured Celery Juice Powder). No exceptions.
            Internal temp stays in the danger zone for hours — nitrite is mandatory.
          </Callout>
          <div className={styles.scheduleTable}>
            {coldSchedule.map((row, i) => (
              <div key={i} className={styles.scheduleRow}>
                <div className={styles.scheduleStep}>{i+1}</div>
                <div className={styles.scheduleTemp}>{row.temp}</div>
                <div className={styles.scheduleDuration}>{row.duration}</div>
                <div className={styles.scheduleNote}>{row.note}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="USDA Internal Temperature Guide">
        <div className={styles.results}>
          {safeTemps.map((r,i) => (
            <ResultRow key={i}
              label={`${r.temp}°F — hold ${r.hold}`}
              value={r.note}
              highlight={r.temp === 145} />
          ))}
        </div>
      </Section>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────
function Section({ title, children }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function InputRow({ label, unit, hint, children }) {
  return (
    <div className={styles.inputRow}>
      <label className={styles.inputLabel}>
        {label}
        {hint && <span className={styles.inputHint}>{hint}</span>}
      </label>
      <div className={styles.inputRight}>
        {children}
        {unit && <span className={styles.inputUnit}>{unit}</span>}
      </div>
    </div>
  );
}

function ResultRow({ label, value, highlight }) {
  return (
    <div className={`${styles.resultRow} ${highlight?styles.resultHighlight:''}`}>
      <span className={styles.resultLabel}>{label}</span>
      <span className={styles.resultValue}>{value}</span>
    </div>
  );
}

function Callout({ type, children }) {
  const icons = { info:'ℹ️', warn:'⚠️', method:'👨‍🍳' };
  const cls   = { info:styles.calloutInfo, warn:styles.calloutWarn, method:styles.calloutMethod };
  return (
    <div className={`${styles.callout} ${cls[type]||''}`}>
      <span className={styles.calloutIcon}>{icons[type]||'ℹ️'}</span>
      <div className={styles.calloutText}>{children}</div>
    </div>
  );
}
